import { formatTimecode } from "../../shared/utils/formatTimecode";
import type { Citation } from "./types";

/**
 * Officer-facing description of one citation: no evidence UUIDs, no
 * storyboard composite/frame nomenclature — those are pipeline internals.
 */
export interface CitationDescription {
  /** "Body-camera video" — the label's human prefix, ids stripped. */
  sourceName: string;
  /** "1:20 – 1:57", collapsed to a single timecode for momentary spans. */
  timeRange: string;
  /**
   * Real quoted material (transcript dialogue). Frame citations have nothing
   * quotable — their citedText is a generated placeholder that must not be
   * rendered as a quote.
   */
  quote?: string;
}

/** The registry builds labels as `<human name> — <evidenceId>[, composite N]`. */
const LABEL_ID_SEPARATOR = " — ";

export function describeCitation(citation: Citation): CitationDescription {
  const separator = citation.sourceLabel.indexOf(LABEL_ID_SEPARATOR);
  const sourceName =
    (separator === -1
      ? citation.sourceLabel
      : citation.sourceLabel.slice(0, separator)
    ).trim() || "Evidence source";

  const start = Number.isFinite(citation.startSeconds)
    ? Math.max(0, citation.startSeconds)
    : 0;
  const end = Number.isFinite(citation.endSeconds)
    ? Math.max(start, citation.endSeconds)
    : start;
  const timeRange =
    end - start >= 1
      ? `${formatTimecode(start)} – ${formatTimecode(end)}`
      : formatTimecode(start);

  return {
    sourceName,
    timeRange,
    ...(citation.locator.kind === "frame" ? {} : { quote: citation.citedText }),
  };
}

/**
 * Distinct evidence items can share a human name ("Body-camera video"); when
 * they collide inside one group, a short id suffix keeps them tellable apart.
 */
export function disambiguationSuffixes(
  citations: readonly Citation[],
): Map<Citation, string> {
  const byName = new Map<string, Set<string>>();
  for (const citation of citations) {
    const { sourceName } = describeCitation(citation);
    const ids = byName.get(sourceName) ?? new Set<string>();
    ids.add(citation.evidenceId);
    byName.set(sourceName, ids);
  }
  const suffixes = new Map<Citation, string>();
  for (const citation of citations) {
    const { sourceName } = describeCitation(citation);
    if ((byName.get(sourceName)?.size ?? 0) > 1) {
      suffixes.set(citation, ` (${citation.evidenceId.slice(0, 8)})`);
    }
  }
  return suffixes;
}
